import styles from './FormModal.module.scss';
import {
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Toolbar,
    IconButton,
    Typography,
    Chip,
    Breakpoint,
    useTheme,
} from '@mui/material';
import {
    FormProvider,
    FormSubmitButton,
    FormProviderProps,
    FormSubmitButtonProps,
} from 'components/form';
import { DefaultOpenModalProps } from 'components/button/OpenModalButton/OpenModalButton';
import { useFormContext, FieldValues } from 'react-hook-form';
import { useFormState } from 'hooks/useFormState';
import { resetFormState, setCurrentSectionIndex } from 'store/features/form';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { Close } from '@mui/icons-material';
import { BaseModal } from 'components/modals/BaseModal/BaseModal';
import { activateSnackbar } from 'store/features/ui';
import { LoadingSpinner } from 'components/data/LoadingSpinner';
import classNames from 'classnames';
import { FormBackButton } from '../FormBackButton/FormBackButton';
interface FormModalProps<TDefaultValues extends FieldValues>
    extends DefaultOpenModalProps {
    title: string | React.ReactNode;
    defaultValues: FormProviderProps<TDefaultValues>['defaultValues'];
    validationSchema: FormProviderProps<TDefaultValues>['validationSchema'];
    children: React.ReactNode;
    actionChildren?: React.ReactNode;
    hideDefaultSubmit?: boolean;
    onErrorHandler?: FormSubmitButtonProps<TDefaultValues>['onErrorHandler'];
    onValidHandler?: FormSubmitButtonProps<TDefaultValues>['onValidHandler'];
    formSections?: string[];
    testId?: string;
    maxWidth?: Breakpoint;
    fullWidth?: boolean;
    onClose?: () => void;
    isLoading?: boolean;
    successMessage?: string;
    /** Remove option for user to click off to close or click an X button to close modal. */
    disallowUserClose?: boolean;
    disallowBackgroundClick?: boolean;
    disallowUserTabChange?: boolean;
    disableFormBackButton?: boolean;
}

/**
 * Abstracted reusable form modal for repeated use across the application
 * @description This component abstracts functionality from various components.
 * Please look at the listed components to understand the underlying api
 * @see OpenModalButton, FormProvider, FormSubmitButton
 * @param {string} title - Title for the modal
 * @param {JSX.Element} actionChildren - action elements
 * @returns FormModal
 */
export function FormModal<TDefaultValues extends FieldValues>({
    defaultValues,
    validationSchema,
    ...props
}: FormModalProps<TDefaultValues>) {
    return (
        <FormProvider
            defaultValues={defaultValues}
            validationSchema={validationSchema}
        >
            <FormDialog {...props} />
        </FormProvider>
    );
}

function FormDialog<TDefaultValues extends FieldValues>({
    modalOpen,
    setModalOpen,
    title,
    children,
    actionChildren,
    hideDefaultSubmit,
    onErrorHandler,
    onValidHandler,
    testId,
    maxWidth,
    fullWidth,
    onClose,
    isLoading,
    successMessage,
    disallowUserClose,
    disallowBackgroundClick = true,
    disallowUserTabChange = true,
    disableFormBackButton,
}: Omit<FormModalProps<TDefaultValues>, 'defaultValues' | 'validationSchema'>) {
    const { reset } = useFormContext();
    const {
        currentFormSectionIndex,
        formSections,
        isFormSubmitDisabled,
        setIsFormSubmitDisabled,
    } = useFormState();

    useEffect(() => {
        setIsFormSubmitDisabled(false);
    }, [setIsFormSubmitDisabled]);

    const dispatch = useDispatch();
    const theme = useTheme();

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleTabPageChange = (
        _: React.ChangeEvent<unknown>,
        value: number
    ) => {
        if (disallowUserTabChange) {
            return;
        } else {
            dispatch(setCurrentSectionIndex(value));
        }
    };

    const formValidHandler: FormSubmitButtonProps<TDefaultValues>['onValidHandler'] =
        async (data) => {
            setIsSubmitting(true);
            if (onValidHandler) {
                await onValidHandler(data);
            }
            handleCloseModal();
            setIsSubmitting(false);
            // Should expect custom handlers to deal with messaging, IMO. This fires all the time even if requests return an error
            if (!onValidHandler) {
                dispatch(
                    activateSnackbar({
                        message: successMessage ?? 'Saved Successfully',
                        severity: 'success',
                    })
                );
            }
        };

    const formErrorHandler: FormSubmitButtonProps<TDefaultValues>['onErrorHandler'] =
        async (data) => {
            setIsSubmitting(true);
            if (onErrorHandler) {
                await onErrorHandler(data);
            }
            setIsSubmitting(false);
        };

    const handleCloseModal = () => {
        setModalOpen(false);
        reset();
        dispatch(resetFormState());
        if (onClose) {
            onClose();
        }
    };

    const closeModal = (event: any, reason: string) => {
        if (disallowBackgroundClick && reason === 'backdropClick') {
            return;
        } else if (
            disallowUserClose &&
            (reason === 'escapeKeyDown' || reason === 'backdropClick')
        ) {
            return;
        }
        handleCloseModal();
    };

    return modalOpen ? (
        <BaseModal
            open={modalOpen}
            onClose={closeModal}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            data-testid={testId}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            disableEscapeKeyDown={disallowUserClose}
        >
            {!isLoading && !disallowUserClose && (
                <Toolbar>
                    <Typography className={styles.modalHeader} variant="h6">
                        {title}
                    </Typography>
                    <IconButton
                        color="inherit"
                        onClick={handleCloseModal}
                        aria-label="close"
                    >
                        <Close />
                    </IconButton>
                </Toolbar>
            )}
            <DialogContent
                data-testid="create-device-modal"
                sx={{ padding: '0px 24px' }} // the default is 20px 24px which leaves a weird vertical empty space.
            >
                {formSections.length > 0 && !isLoading && (
                    <Tabs
                        value={currentFormSectionIndex}
                        onChange={handleTabPageChange}
                        aria-label="tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                        className={styles.tabs}
                        sx={{
                            backgroundColor: theme.palette.background.default,
                        }}
                    >
                        {formSections.map((page, index) => (
                            <Tab
                                key={page}
                                label={page}
                                icon={
                                    formSections.length > 1 ? (
                                        <Chip
                                            label={index + 1}
                                            color={
                                                currentFormSectionIndex ===
                                                index
                                                    ? 'primary'
                                                    : 'default'
                                            }
                                        />
                                    ) : undefined
                                }
                                iconPosition="start"
                            />
                        ))}
                    </Tabs>
                )}
                <div
                    className={classNames(styles.modalBody, {
                        [styles.isLoading]: isLoading,
                    })}
                >
                    {isLoading ? (
                        <LoadingSpinner className={styles.loadingSpinner} />
                    ) : (
                        children
                    )}
                </div>
            </DialogContent>
            {!isLoading && (
                <DialogActions className={styles.modalActions}>
                    <FormBackButton disabled={disableFormBackButton} />
                    {actionChildren}
                    {!hideDefaultSubmit && (
                        <FormSubmitButton
                            onErrorHandler={formErrorHandler}
                            onValidHandler={formValidHandler}
                            isLoading={isSubmitting}
                            disabled={isFormSubmitDisabled}
                        />
                    )}
                </DialogActions>
            )}
        </BaseModal>
    ) : null;
}

interface MockFormModalProps {
    children: React.ReactNode;
    removeValidation?: boolean;
}

export function MockFormModal({
    removeValidation,
    children,
}: MockFormModalProps) {
    const [modalOpen, setModalOpen] = useState(false);
    const mockDefaultValues = {
        stringField: '',
        selectField: '',
        numberField: '',
        booleanField: false,
        multiSelectField: [],
        dateField: new Date(),
    };

    const mockValidationSchema = Yup.object({
        stringField: Yup.string().min(1).max(10).required(),
        numberField: Yup.number().min(2).max(8).required(),
        booleanField: Yup.boolean().required(),
        multiSelectField: Yup.array().min(1).required(),
        selectField: Yup.string().required(),
        dateField: Yup.date().required(),
    }).required();

    return (
        <>
            <button
                data-testid="toggle-modal-button"
                onClick={() => setModalOpen(!modalOpen)}
            >
                open modal
            </button>
            <FormModal
                defaultValues={{
                    ...mockDefaultValues,
                }}
                validationSchema={
                    removeValidation ? Yup.object({}) : mockValidationSchema
                }
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
                onValidHandler={(mockFormData) => {
                    return mockFormData;
                }}
                onErrorHandler={(data) => {
                    return data;
                }}
                title="Configure Monitor"
            >
                {children}
            </FormModal>
        </>
    );
}
